import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Bell } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { newsArticles } from '../../data/news';

const NewsEvents = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  // Set to false to show the actual news content when ready
  const showComingSoon = true;

  const categories = ['all', 'Achievement', 'Facilities', 'Sports', 'Events'];

  const filteredNews = activeFilter === 'all'
    ? newsArticles
    : newsArticles.filter(article => article.category === activeFilter);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <>
      <PageHeader
        title="News & Events"
        subtitle="Stay updated with the latest happenings at Stirret Schools"
        breadcrumbs={[{ label: 'News & Events' }]}
      />

      {/* Coming Soon Overlay */}
      {showComingSoon && (
        <section className="section-padding bg-gradient-to-br from-primary-50 to-blue-50 min-h-[60vh] flex items-center">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl mx-auto text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
                className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-8"
              >
                <Bell className="text-primary-600" size={48} />
              </motion.div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-6">
                Coming Soon
              </h2>
              <p className="text-lg md:text-xl text-gray-600 mb-8">
                We're working on bringing you the latest news and events from Stirret Schools.
                Stay tuned for updates on our achievements, activities, and upcoming events!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact" className="btn-primary">
                  Contact Us for Updates
                </Link>
                <Link to="/" className="btn-secondary">
                  Back to Home
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* News Content - Hidden when showComingSoon is true */}
      {!showComingSoon && (
        <section className="section-padding bg-white">
          <div className="container-custom">
            {/* Filters */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveFilter(category)}
                  className={`px-5 py-2 rounded-full font-medium transition-colors capitalize ${
                    activeFilter === category
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {category === 'all' ? 'All News' : category}
                </button>
              ))}
            </div>

            {/* News Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredNews.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-hard transition-all duration-300"
                >
                  <Link to={`/news/${article.slug}`}>
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-primary-600">
                          {article.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                        <Calendar size={14} />
                        {formatDate(article.date)}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 line-clamp-3 mb-4">{article.excerpt}</p>
                      <div className="flex items-center text-primary-600 font-semibold group-hover:gap-2 transition-all">
                        Read More
                        <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" size={18} />
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>

            {filteredNews.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No news articles found in this category.</p>
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
};

export default NewsEvents;
